import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SuccessAlertContext } from "App";
import Notify from "config/notify";
import { CSVCountCheckStock, CSVThresholdCheckStock } from "../types";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "./TableCellSmall";
import { SelectForm } from "./Common";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 33,
  },
};

const tableHeadthreshold = (error?: string) => (
  <TableHead>
    <TableRow>
      <TableHeaderCellSmall rowSpan={2}>商品コード</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>JANコード</TableHeaderCellSmall>
      <TableHeaderCellSmall colSpan={2} sx={styles.supplierTopCol}>
        在庫数設定値
      </TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>在庫数表示</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>廃番</TableHeaderCellSmall>
      {error && <TableHeaderCellSmall rowSpan={2}>エラー内容</TableHeaderCellSmall>}
    </TableRow>
    <TableRow>
      <TableHeaderCellSmall sx={styles.supplierBottomCol}>○~△境界値</TableHeaderCellSmall>
      <TableHeaderCellSmall sx={styles.supplierBottomCol}>△~×境界値</TableHeaderCellSmall>
    </TableRow>
  </TableHead>
);

const tableBodyThreshold = (s: CSVThresholdCheckStock, error?: string) => (
  <TableRow key={s.productCd} sx={error && s.error ? { backgroundColor: "var(--red-400)" } : {}}>
    <TableCellSmall>{s.productCd}</TableCellSmall>
    <TableCellSmall>{s.JANCd}</TableCellSmall>
    {s.symbolThresholdList.length >= 2 && (
      <>
        <TableCellSmall
          sx={{ textAlign: "right" }}
        >{`${s.symbolThresholdList[0].toLocaleString()}`}</TableCellSmall>
        <TableCellSmall
          sx={{ textAlign: "right" }}
        >{`${s.symbolThresholdList[1].toLocaleString()}`}</TableCellSmall>
      </>
    )}
    <TableCellSmall>{s.stockVisibleType}</TableCellSmall>
    <TableCellSmall>
      <Checkbox checked={s.isDiscontinued} disabled />
    </TableCellSmall>
    {error &&
      (s.error ? (
        <TableCellWide>
          <MultiTypography>{s.error}</MultiTypography>
        </TableCellWide>
      ) : (
        <TableCellWide />
      ))}
  </TableRow>
);

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: Function;
  stockList: (CSVCountCheckStock | CSVThresholdCheckStock)[];
  isThreshold?: boolean;
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  setInit?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  error?: string;
  errorTitle?: string;
  // 画面からupdateする際に仕入先を見る時使用
  isSupplier?: boolean; // CSVではないupdateの時のみ仕入先ユーザーであればisSupplierに入力
  selectHeaderCenterWarehouse?: () => JSX.Element; // ヘッダーでセンター倉庫を選ぶ要素
  selectHeaderRegionWarehouse?: () => JSX.Element; // 近隣倉庫を選ぶ要素
  bodyCenterWarehouse?: (id: string) => JSX.Element; // 選ばれたときの対象のセンター倉庫数量
  bodyRegionWarehouse?: (id: string) => JSX.Element; // 選ばれたときの対象の近隣倉庫数量
};
/**
 * 在庫数量更新確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function SupplierConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  stockList,
  isThreshold,
  isFastLoading,
  getMoreStockList,
  setInit,
  isLoading,
  error,
  errorTitle,
  // 画面からupdateする際に仕入先を見る時使用
  isSupplier,
  selectHeaderCenterWarehouse,
  selectHeaderRegionWarehouse,
  bodyCenterWarehouse,
  bodyRegionWarehouse,
}: Props) {
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
    // CSVの仕入先の場合に使用
  const [selectedRegionWarehouseIndex, setSelectedRegionWarehouseIndex] = useState<number>(0); // 地域倉庫のindex(supplierWarehouseのindexではなく!isCenterでfilterされたindex)
  const [selectedCenterWarehouseIndex, setSelectedCenterWarehouseIndex] = useState<number>(0); // センターのindex(supplierWarehouseのindexではなくisCenterでfilterされたindex)
  const { showAlert } = useContext(SuccessAlertContext);

  // スクロール処理
  const handleScroll = async () => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight + 10 >= scrollHeight) {
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  const selectHeaderCenterWarehouseCsv = () => {
    const sList = stockList as CSVCountCheckStock[];
    if (sList && sList.length > 0 && sList[0].supplierWarehouses && sList[0].supplierWarehouses.length > 0 && sList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0) {
      const supplierWarehouses = sList[0].supplierWarehouses.filter((sw) => sw.isCenter);
      return (
        <TableHeaderCellWide sx={{...styles.supplierTopCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
          <SelectForm
            id="center_warehouse"
            label="センター倉庫"
            optionList={supplierWarehouses.map(sw => sw.name)}
            value={supplierWarehouses[selectedCenterWarehouseIndex].name}
            setValue={(val: string) => setSelectedCenterWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
          />
        </TableHeaderCellWide>
      )
    }
    return <Box/>;
  };

  const selectHeaderRegionWarehouseCsv = () => {
    const sList = stockList as CSVCountCheckStock[];
    if (sList && sList.length > 0 && sList[0].supplierWarehouses && sList[0].supplierWarehouses.length > 0 && sList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0) {
      // 地域倉庫がある場合表示
      const supplierWarehouses = sList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
      return (
        <TableHeaderCellWide sx={{...styles.supplierTopCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
          <SelectForm
            id="region_warehouse"
            label="地域倉庫"
            optionList={supplierWarehouses.map(sw => sw.name)}
            value={supplierWarehouses[selectedRegionWarehouseIndex].name}
            setValue={(val: string) => setSelectedRegionWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
          />
        </TableHeaderCellWide>
      )
    }
    return <Box/>;
  };

  const supplierHeader = () => selectHeaderCenterWarehouse && selectHeaderRegionWarehouse ? (<>
    {selectHeaderCenterWarehouse()}
    {selectHeaderRegionWarehouse()}
  </>) : (<>
    {selectHeaderCenterWarehouseCsv()}
    {selectHeaderRegionWarehouseCsv()}
  </>);

  // 現在選択されているセンター倉庫数量
  const bodyCenterWarehouseCsv = (id: string) => {
    const sList = stockList as CSVCountCheckStock[];
    const stock = sList.find(s => s.id === id);
    return stock && isSupplier && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0
      // ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
      ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount).toLocaleString()}</TableCellNarrow>
      : <Box/>
  };
  // 現在選択されている地域倉庫数量
  const bodyRegionWarehouseCsv = (id: string) => {
    const sList = stockList as CSVCountCheckStock[];
    const stock = sList.find(s => s.id === id);
    return stock && isSupplier && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0
      ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount).toLocaleString()}</TableCellNarrow>
      : <Box/>
  };

  const supplierBody = (s: CSVCountCheckStock) => bodyCenterWarehouse && bodyRegionWarehouse ? (<>
    {s.id ? bodyCenterWarehouse(s.id) : <Box/>}
    {s.id ? bodyRegionWarehouse(s.id) : <Box/>}
  </>) : (<>
    {s.id ? bodyCenterWarehouseCsv(s.id) : <Box/>}
    {s.id ? bodyRegionWarehouseCsv(s.id) : <Box/>}
  </>);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [fireMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      // if (error) showAlert(error, "error");
      if (error) {
        Notify?.setNotification?.({
          show: true,
          title: "CSVアップロードでエラーがあります。",
          message: error,
          messageWhiteSpace: "pre-line",
        });
      }
    }
  }, [open]);

  // 表示数がlimitの時、中のコンテンツが小さいと無限ローディングさせることができないため、少しスクロールできるようにする。
  useEffect(() => {
    if (stockList.length === limit && !isLoading) {
      // console.log('adjustment', adjustment, stockList.length, contentRef.current?.clientHeight);
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      setAdjustment(undefined);
    }
  }, [stockList, isLoading]);
  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {stockList.length !== 0 &&
                (isThreshold ? (
                  tableHeadthreshold(error)
                ) : (
                  <TableHead>
                    <TableRow>
                      <TableHeaderCellSmall>商品コード</TableHeaderCellSmall>
                      <TableHeaderCellSmall>JANコード</TableHeaderCellSmall>
                      { isSupplier
                        ? supplierHeader()
                        : <TableHeaderCellSmall>在庫数量</TableHeaderCellSmall>
                      }
                      <TableHeaderCellSmall>入荷予定日</TableHeaderCellSmall>
                      <TableHeaderCellSmall>倉庫コード</TableHeaderCellSmall>
                      {error && <TableHeaderCellSmall>エラー内容</TableHeaderCellSmall>}
                    </TableRow>
                  </TableHead>
                ))}

              <TableBody>
                {isThreshold
                  ? stockList.map((s) => tableBodyThreshold(s as CSVThresholdCheckStock, error))
                  : stockList.map((s) => {
                      // eslint-disable-next-line no-param-reassign
                      s = s as CSVCountCheckStock;
                      return (
                        <TableRow
                          key={s.productCd}
                          sx={error && s.error ? { backgroundColor: "var(--red-400)" } : {}}
                        >
                          <TableCellSmall>{s.productCd}</TableCellSmall>
                          <TableCellSmall>{s.JANCd}</TableCellSmall>
                          { isSupplier
                            ? supplierBody(s)
                            : (
                              <TableCellSmall>
                                {typeof s.amount === "number" ? s.amount.toLocaleString() : s.amount}
                              </TableCellSmall>
                            )
                          }
                          <TableCellSmall>{s.arrivalDate}</TableCellSmall>
                          {"unusedStorehouseCd" in s ? (
                            <TableCellSmall>{s.unusedStorehouseCd}</TableCellSmall>
                          ) : (
                            <TableCellSmall />
                          )}
                          {error &&
                            (s.error ? (
                              <TableCellWide>
                                <MultiTypography>{s.error}</MultiTypography>
                              </TableCellWide>
                            ) : (
                              <TableCellWide />
                            ))
                          }
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          {adjustment && ( // 画面が大きいと無限スクロールが発火しないことがある
            <Box height={adjustment} />
          )}
          {isLoading && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>{ActionButtons}</DialogActions>
    </Dialog>
  );
}
